import React, { useState } from "react";

import { faBarsFilter, faCalendarDays, faSearch } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faSparkles } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonIcon, ButtonStandard, InputText } from "@performyard/py-components";
import type { MeetingModelJson } from "@performyard/service-contracts";

import { AIPromotionalButton } from "../../../../components/AIModules/AIPromotionalButton/AIPromotionalButton";
import { MeetingWorkspaceInsightModule } from "../../../../components/AIModules/MeetingWorkspaceInsight";
import PageActionsDropdown from "./PageActionsDropdown";

import { useAiSettings } from "../../../../api/hooks";
import { useAiPromotion } from "../../../../hooks/useAiPromotion";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import { config } from "../config";
import type { MeetingsTableFilters } from "../schema";

type ToolbarProps = {
	searchValue: string;
	onSearchChange: (value: string) => void;
	onFilterClick: () => void;
	isFilterDisabled: boolean;
	onAddMeetingClick: () => void;
	workspaceId: string;
	filters: MeetingsTableFilters;
	meetings: MeetingModelJson[];
	userId: string;
	isAdmin: boolean;
	isTeamWorkspace: boolean;
};

const Toolbar = ({
	searchValue,
	onSearchChange,
	onFilterClick,
	isFilterDisabled,
	onAddMeetingClick,
	workspaceId,
	filters,
	meetings,
	userId,
	isAdmin,
	isTeamWorkspace,
}: ToolbarProps): React.ReactElement => {
	const { data: aiSettings } = useAiSettings();
	const { value: isTeamMeetingsFeatureEnabled } = useFeatureFlagQuery("ff-is-team-meetings-enabled", false);
	// Default to false while settings load so the button doesn't flash on disabled companies.
	const isAIInsightsEnabled = isTeamWorkspace
		? isTeamMeetingsFeatureEnabled && (aiSettings?.teamMeetingWorkspaceInsightsEnabled ?? false)
		: (aiSettings?.meetingWorkspaceInsightsEnabled ?? false);
	const { isAiPromotion, promotionId, userEmail } = useAiPromotion();
	// The insight filter contract is strict and has no series filter of its own.
	const { recurringSeriesId: _recurringSeriesId, ...insightableFilters } = filters;
	const { privateNotes: _privateNotes, ...teamInsightFilters } = insightableFilters;
	const insightFilters = isTeamWorkspace ? teamInsightFilters : insightableFilters;

	const hasNoValidMeetings = meetings.length === 0;
	const allMeetingsCancelled = meetings.length > 0 && meetings.every((meeting) => meeting.status === "canceled");
	const isAIInsightsDisabled = hasNoValidMeetings;

	const [isAIInsightsOpen, setIsAIInsightsOpen] = useState<boolean>(false);

	return (
		<>
			<div className="flex flex-row justify-between">
				<div className="flex items-center space-x-2">
					<div style={{ width: config.layout.searchInputWidth }}>
						<InputText
							leftIcon={faSearch}
							value={searchValue}
							onChange={onSearchChange}
							placeholder="Search meetings..."
							aria-label="Search meetings"
							clearable
							data-testid="meetingsSearchInput"
						/>
					</div>
					<div className="relative">
						<ButtonIcon
							variant="outline"
							size={32}
							icon={faBarsFilter}
							onClick={onFilterClick}
							disabled={isFilterDisabled}
							aria-label="Open filters"
						/>
					</div>
				</div>
				<div className="flex items-center space-x-2">
					{isAIInsightsEnabled && (
						<ButtonStandard
							color="primary"
							iconLeft={faSparkles}
							onClick={() => setIsAIInsightsOpen(true)}
							disabled={isAIInsightsDisabled}
						>
							AI Insights
						</ButtonStandard>
					)}
					{isAiPromotion && !isTeamWorkspace && (
						<AIPromotionalButton
							buttonSize="medium"
							label="AI Insights"
							promotionId={promotionId}
							userEmail={userEmail}
						/>
					)}
					<ButtonStandard color="green" iconLeft={faCalendarDays} onClick={onAddMeetingClick}>
						Add Meeting
					</ButtonStandard>
					<PageActionsDropdown workspaceId={workspaceId} filters={filters} userId={userId} />
				</div>
			</div>
			{isAIInsightsEnabled && (
				<MeetingWorkspaceInsightModule
					workspaceId={workspaceId}
					isOpen={isAIInsightsOpen}
					onOpenChange={setIsAIInsightsOpen}
					filters={insightFilters}
					isAdmin={isAdmin}
					allMeetingsCancelled={allMeetingsCancelled}
					isTeamWorkspace={isTeamWorkspace}
				/>
			)}
		</>
	);
};

export default Toolbar;
